import { useEffect, useState } from "react";
import { ImageUp, Palette, Save } from "lucide-react";

import { useBrand } from "@/context/BrandContext";
import { api, apiError } from "@/lib/api";

const colorLabels = {
  primary: "Azul principal",
  accent: "Azul de acento",
  background: "Fondo general",
  surface: "Superficie de tarjetas",
};
const allowedImageTypes = ["image/png", "image/jpeg", "image/webp"];

export default function BrandSettingsPanel() {
  const { brand, updateBrand } = useBrand();
  const [colors, setColors] = useState(brand.colors);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setColors(brand.colors);
  }, [brand]);

  const upload = async (kind, file) => {
    if (!file) return;
    const maximumSize = kind === "logo" ? 2_000_000 : 8_000_000;
    if (!allowedImageTypes.includes(file.type)) {
      setMessage("Selecciona una imagen PNG, JPG o WEBP.");
      return;
    }
    if (file.size > maximumSize) {
      const limit = kind === "logo" ? "2 MB" : "8 MB";
      setMessage(`La imagen seleccionada supera el límite de ${limit}.`);
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setMessage("");
    try {
      const response = await api.post(`/brand/assets/${kind}`, form);
      updateBrand(response.data);
      setMessage(kind === "logo" ? "Logo actualizado." : "Imagen de acceso actualizada.");
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  const saveColors = async (event) => {
    event.preventDefault();
    setMessage("");
    try {
      const response = await api.put("/brand/colors", colors);
      updateBrand(response.data);
      setMessage("Paleta aplicada en todo el sistema.");
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  return (
    <section className="brand-settings-panel" data-testid="brand-settings-panel">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">IDENTIDAD VISUAL</span>
          <h2>Marca y colores</h2>
        </div>
        <Palette size={20} />
      </div>
      <div className="brand-upload-grid">
        <label className="brand-preview" htmlFor="brand-logo-upload">
          <img alt="Logo actual" data-testid="brand-logo-preview" src={brand.logo_url} />
          <span><ImageUp size={15} /> Reemplazar logo</span>
          <input accept="image/png,image/jpeg,image/webp" data-testid="brand-logo-upload" id="brand-logo-upload" onChange={(event) => upload("logo", event.target.files?.[0])} type="file" />
        </label>
        <label className="brand-preview login-preview" htmlFor="brand-login-upload">
          <img alt="Imagen actual de acceso" data-testid="brand-login-preview" src={brand.login_image_url} />
          <span><ImageUp size={15} /> Reemplazar imagen de acceso</span>
          <input accept="image/png,image/jpeg,image/webp" data-testid="brand-login-upload" id="brand-login-upload" onChange={(event) => upload("login", event.target.files?.[0])} type="file" />
        </label>
      </div>
      <form className="brand-colors-form" data-testid="brand-colors-form" onSubmit={saveColors}>
        {Object.entries(colors).map(([name, value]) => (
          <label htmlFor={`brand-color-${name}`} key={name}>
            {colorLabels[name]}
            <span>
              <input
                data-testid={`brand-color-${name}`}
                id={`brand-color-${name}`}
                onChange={(event) => setColors({ ...colors, [name]: event.target.value })}
                type="color"
                value={value}
              />
              <code>{value}</code>
            </span>
          </label>
        ))}
        <button className="primary-button" data-testid="brand-colors-save-button" type="submit"><Save size={16} /> Guardar paleta</button>
      </form>
      {message && <p className="form-note" data-testid="brand-settings-message">{message}</p>}
    </section>
  );
}